import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Position } from 'geojson';
import type { Layer } from '@deck.gl/core';
import { calculateCentroid } from '../../../utils/geospatial';
import { toTitle } from '../../../utils/case';
import { IconLayer } from '@deck.gl/layers';
import { useMapVisibility } from '../useMapVisibility';
import { useAreasData } from '../../data/useAreasData';
import { useCustomAreas } from '../useCustomAreas';
import type { Area, CustomArea } from '../../../types/domain';
import { useDataFilter } from '../useDataFilter';

type PinData = {
  position: Position;
  tripFilter: 'start' | 'end' | 'excursion' | 'pass' | null;
  agentFilter: 'home' | 'work' | 'visit' | null;
  tooltip: string;
};

type UseFilterPinLayer = {
  filterPinLayer: Layer;
};

const useFilterPinLayer = (): UseFilterPinLayer => {
  const { t } = useTranslation();
  const { dataFilter, filteredAreaKeyCodes, filteredCustomAreaKeys } =
    useDataFilter();
  const { areaTowns, areaCities } = useAreasData();
  const { visibleCustomAreas } = useCustomAreas();
  const { visibleAreaType } = useMapVisibility();

  const data = useMemo((): PinData[] => {
    return [
      ...areaTowns.values(),
      ...areaCities.values(),
      ...visibleCustomAreas,
    ]
      .filter((area: Area | CustomArea): boolean => {
        return 'key_code' in area
          ? filteredAreaKeyCodes.includes(area.key_code)
          : filteredCustomAreaKeys.includes(area.key);
      })
      .map((area: Area | CustomArea): PinData => {
        const filter =
          'key_code' in area
            ? {
                start: dataFilter.start.areaKeyCodes.includes(area.key_code),
                end: dataFilter.end.areaKeyCodes.includes(area.key_code),
                pass: dataFilter.pass.areaKeyCodes.includes(area.key_code),
                home: dataFilter.home.areaKeyCodes.includes(area.key_code),
                work: dataFilter.work.areaKeyCodes.includes(area.key_code),
                visit: dataFilter.visit.areaKeyCodes.includes(area.key_code),
              }
            : {
                start: dataFilter.start.customAreaKeys.includes(area.key),
                end: dataFilter.end.customAreaKeys.includes(area.key),
                pass: dataFilter.pass.customAreaKeys.includes(area.key),
                home: dataFilter.home.customAreaKeys.includes(area.key),
                work: dataFilter.work.customAreaKeys.includes(area.key),
                visit: dataFilter.visit.customAreaKeys.includes(area.key),
              };
        const tripFilter: PinData['tripFilter'] =
          filter.start && filter.end
            ? 'excursion'
            : filter.start
              ? 'start'
              : filter.end
                ? 'end'
                : filter.pass
                  ? 'pass'
                  : null;
        const agentFilter: PinData['agentFilter'] = filter.home
          ? 'home'
          : filter.work
            ? 'work'
            : filter.visit
              ? 'visit'
              : null;

        return {
          position:
            'key_code' in area
              ? [area.lon, area.lat]
              : calculateCentroid({
                  type: 'Polygon',
                  coordinates: [area.geometry.coordinates],
                }),
          tripFilter: tripFilter,
          agentFilter: agentFilter,
          tooltip: `${tripFilter ? t(`trip${toTitle(tripFilter)}`, { defaultValue: '' }) : ''}${tripFilter && agentFilter ? t('listDelimiter') : ''}${agentFilter ? t(agentFilter) : ''}`,
        };
      });
  }, [
    t,
    areaTowns,
    areaCities,
    dataFilter.start,
    dataFilter.end,
    dataFilter.pass,
    dataFilter.home,
    dataFilter.work,
    dataFilter.visit,
    filteredAreaKeyCodes,
    filteredCustomAreaKeys,
    visibleCustomAreas,
  ]);

  const filterPinLayer = new IconLayer<PinData>({
    id: 'filter-pin-layer',
    data: data,
    getPosition: (data: PinData): [number, number] => {
      return [data.position[0], data.position[1]];
    },
    getIcon: (data: PinData) => {
      return {
        url:
          data.tripFilter && data.agentFilter
            ? `/icons/pin_${data.tripFilter}_${data.agentFilter}.png`
            : data.tripFilter
              ? `/icons/pin_${data.tripFilter}.png`
              : data.agentFilter
                ? `/icons/pin_${data.agentFilter}.png`
                : '',
        height: 250,
        width: 250,
        mask: false,
      };
    },
    getPixelOffset: [0, -20],
    getSize: 40,
    pickable: true,
    visible: !!visibleAreaType,
  });

  return {
    filterPinLayer: filterPinLayer,
  };
};

export { useFilterPinLayer };
